<template>
  <v-dialog
    v-if="value"
    :value="value"
    class="addCamera"
    eager
    persistent
    width="600"
    @keydown.esc="closeDialog"
  >
    <v-card tile>
      <ValidationObserver ref="obs" v-slot="{ invalid }">
        <v-card-title>
          {{ editOrCreateText }} Application
          <v-btn
            class="close-icon"
            color="primary"
            icon
            dark
            large
            @click="closeDialog"
          >
            <v-icon>fa-times</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <ERow no-gutters>
            <ECol cols="12" class="mt-4">
              <Autocomplete
                v-model="selectedProject"
                item-value="id"
                item-text="name"
                label="Project"
                clearable
                search-on-mounted
                is-combobox
                rules="required"
                :provider="getProjects"
                :provider-params="projectParams"
                :disabled="editMode"
              />
            </ECol>
            <ECol cols="12" class="mt-2">
              <ValidationProvider
                v-slot="{ errors, valid }"
                rules="required"
                name="Anpr Camera"
              >
                <v-select
                  v-model="selectedCameraExid"
                  item-value="exid"
                  :items="anprCameras"
                  item-text="name"
                  label="ANPR Camera"
                  outlined
                  dense
                  :error-messages="errors"
                  :success="valid"
                  :menu-props="{ offsetY: true }"
                  :disabled="editMode"
                />
              </ValidationProvider>
            </ECol>
            <ECol cols="12" class="mt-2">
              <ValidationProvider
                v-slot="{ errors, valid }"
                name="Anpr Context Camera"
                rules="required"
              >
                <v-select
                  v-model="selectedContextCameraExid"
                  item-value="exid"
                  :items="contextCameras"
                  item-text="name"
                  label="ANPR Context Camera"
                  outlined
                  dense
                  :error-messages="errors"
                  :success="valid"
                  :menu-props="{ offsetY: true }"
                  @input="onEditApp('contextCameraExid', $event)"
                />
              </ValidationProvider>
            </ECol>
            <ECol cols="12" class="mt-2">
              <ValidationProvider
                v-slot="{ errors, valid }"
                rules="required"
                name="Coolify Server"
              >
                <v-select
                  v-model="selectedServerUuid"
                  item-value="uuid"
                  item-text="name"
                  :items="coolifyServices"
                  outlined
                  label="Coolify Server"
                  :error-messages="errors"
                  :success="valid"
                  :menu-props="{ offsetY: true }"
                  dense
                  :disabled="editMode"
                />
              </ValidationProvider>
            </ECol>
            <ECol cols="12" class="mt-2">
              <ValidationProvider
                v-slot="{ errors, valid }"
                rules="required"
                name="App Version"
              >
                <v-text-field
                  v-model="branchName"
                  class="caption"
                  :error-messages="errors"
                  :success="valid"
                  label="App Version (Docker Tag)"
                  placeholder="e.g. master"
                  dense
                  outlined
                  required
                  @input="onEditApp('branchName', $event)"
                >
                </v-text-field>
              </ValidationProvider>
            </ECol>
            <ECol cols="12" class="mt-2">
              <ValidationProvider
                v-slot="{ errors, valid }"
                rules="required"
                name="Snapshot Request Timeout"
              >
                <v-text-field
                  v-model="snapshotRequestTimeout"
                  class="caption"
                  :error-messages="errors"
                  :success="valid"
                  label="Snapshot Request Timeout (in seconds)"
                  placeholder="e.g. master"
                  dense
                  outlined
                  required
                  @input="onEditApp('snapshotRequestTimeout', $event)"
                >
                </v-text-field>
              </ValidationProvider>
            </ECol>
            <ECol cols="12" class="mt-2">
              <ValidationProvider
                v-slot="{ errors, valid }"
                rules="required"
                name="Model Version"
              >
                <v-text-field
                  v-model="modelVersion"
                  class="caption"
                  :error-messages="errors"
                  :success="valid"
                  label="Model Version"
                  placeholder="e.g. master"
                  dense
                  outlined
                  required
                  @input="onEditApp('modelVersion', $event)"
                >
                </v-text-field>
              </ValidationProvider>
            </ECol>
            <ECol cols="12" class="mt-2">
              <ValidationProvider
                v-slot="{ errors }"
                rules="required"
                name="Start Date"
              >
                <VueCtkDateTimePicker
                  v-model="startTime"
                  label="Start date"
                  format="YYYY-MM-DDTHH:mm:ss"
                  formatted="YYYY-MM-DD HH:mm:ss"
                  output-format="YYYY-MM-DDTHH:mm:ss"
                  name="startDate"
                  :error="!!errors.length"
                  @input="onEditApp('startTime', $event)"
                ></VueCtkDateTimePicker>
                <div v-if="errors[0]" class="error--text">
                  {{ errors[0] }}
                </div>
              </ValidationProvider>
            </ECol>
          </ERow>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeDialog">Close</v-btn>
          <v-btn
            color="primary"
            :loading="isLoading"
            :disabled="isLoading || invalid"
            text
            @click="createOrUpdateCoolify"
            >{{ editOrCreateText }}
          </v-btn>
        </v-card-actions>
      </ValidationObserver>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import Vue from "vue"
import Autocomplete from "@evercam/shared/components/Autocomplete"
import { AdminApi, AiApi } from "@evercam/shared/api"
import {
  CameraFeatureFlag,
  CreateCoolifyAppRequestPayload,
  ProjectUpdateRequestPayload,
  SaveCoolifyAppRequestPayload,
  ProjectFeatureFlag,
  Camera,
  type CameraUpdateRequestPayload,
  CameraStatus,
} from "@evercam/shared/types"

export default Vue.extend({
  name: "AnprGateReportCreateUpdateAppDialog",
  components: { Autocomplete },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    appData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      selectedProject: null,
      selectedProjectName: "",
      selectedCameraExid: null,
      selectedProjectFeatureFlags: [],
      selectedProjectExid: null,
      selectedContextCameraExid: null,
      selectedServerUuid: null,
      coolifyServices: [],
      cameras: [],
      contextCameras: [],
      anprCameras: [],
      branchName: "master",
      snapshotRequestTimeout: 300,
      modelVersion: "2024-10-12",
      startTime: this.$moment().format("YYYY-MM-DDTHH:mm:ss"),
      coolifyServiceId: null,
      isLoading: false,
      EditedObject: {},
    }
  },
  computed: {
    editOrCreateText(): string {
      return this.editMode ? "Edit" : "Create"
    },
    getCameras(): any {
      return AdminApi.cameras.getCameras
    },
    getProjects(): any {
      return AdminApi.projects.index
    },
    shouldUpdateProjectFeatureFlags(): boolean {
      if (!this.selectedProjectFeatureFlags.length) {
        return false
      }

      return (
        this.selectedProjectFeatureFlags.includes(
          ProjectFeatureFlag.GateReport
        ) &&
        this.selectedProjectFeatureFlags.includes(
          ProjectFeatureFlag.GateReportAnprBased
        )
      )
    },
    anprCamera(): Camera | null {
      return (
        this.cameras.find(
          (camera) => camera.exid === this.selectedCameraExid
        ) || null
      )
    },
    contextCamera(): Camera | null {
      return this.cameras.find(
        (camera) => camera.exid === this.selectedContextCameraExid
      )
    },
    cameraHasAnprFeatureFlag(): boolean {
      return this.anprCamera.featureFlags.includes(CameraFeatureFlag.ANPR)
    },
    cameraHasGateReportFeatureFlag(): boolean {
      return this.contextCamera.featureFlags.includes(
        CameraFeatureFlag.GateReport
      )
    },
  },
  watch: {
    selectedProjectName: {
      async handler(val, oldVal) {
        if (!val || val === oldVal) {
          return
        }

        const results = await AdminApi.cameras.getCameras({
          params: {
            sort: "created_at|asc",
            limit: 50,
            page: 1,
            projectName: val,
          },
        })

        this.cameras = results.items.filter(
          (camera) => camera.status !== CameraStatus.Decommissioned
        )
        this.anprCameras = this.cameras
        this.contextCameras = this.cameras
      },
    },
    selectedProject: {
      handler(val, oldVal) {
        if (!val || val === oldVal) {
          return
        }

        this.selectedProjectExid = val.exid
        this.selectedProjectName = val.name
        this.selectedProjectFeatureFlags = val.featureFlags
      },
      immediate: true,
    },
    selectedCameraExid(val, oldVal) {
      if (!val || val === oldVal) {
        return
      }

      this.contextCameras = this.cameras.filter((camera) => camera.exid !== val)
    },
    selectedContextCameraExid(val, oldVal) {
      if (!val || val === oldVal) {
        return
      }

      this.anprCameras = this.cameras.filter((camera) => camera.exid !== val)
    },
    appData: {
      handler(val) {
        if (val && this.editMode) {
          this.onInitializeData()
        }
      },
      immediate: true,
    },
    editMode: {
      handler(val) {
        if (val && this.appData) {
          this.onInitializeData()
        } else {
          this.resetForm()
        }
      },
    },
  },
  mounted() {
    this.getCoolifyServers()
    this.EditedObject = {}
  },
  methods: {
    onInitializeData() {
      this.selectedProject = this.appData.project
      this.selectedCameraExid = this.appData.exid
      this.selectedContextCameraExid = this.appData.contextCameraExid
      this.selectedServerUuid = this.appData.coolifyServerUuid
      this.branchName = this.appData.branchName
      this.snapshotRequestTimeout = this.appData.snapshotRequestTimeout
      this.modelVersion = this.appData.modelVersion
      this.startTime = this.$moment(this.appData.startTime).format(
        "YYYY-MM-DDTHH:mm:ss"
      )
    },
    async getCoolifyServers() {
      this.coolifyServices = await AiApi.coolify.getCoolifyServers()
    },
    openDialog() {
      this.$emit("input", true)
    },
    closeDialog() {
      if (this.editMode) {
        this.resetForm()
      }
      this.$emit("input", false)
    },
    projectParams(val): Record<string, any> {
      return {
        sort: "created_at|asc",
        limit: 50,
        page: 1,
        name: val,
      }
    },
    async UpdateProjectFeatureFlags(
      projectExid,
      params: Partial<ProjectUpdateRequestPayload>
    ) {
      await AdminApi.projects.updateProject(projectExid, params)
    },
    async updateCameraFeatureFlags(
      cameraExid,
      params: Partial<CameraUpdateRequestPayload>
    ) {
      const anprCamera = await AdminApi.cameras.update(cameraExid, params)
      console.warn("Camera updated (feature flag added)", {
        params,
        anprCamera,
      })
    },
    async createOrUpdateCoolify() {
      if (this.editMode) {
        await this.updateApp()
      } else {
        await this.createAppOnCoolify()
      }
      this.resetForm()
      this.EditedObject = {}
      this.$root.$emit("refresh-report-table")
      this.closeDialog()
    },
    async updateApp() {
      this.isLoading = true

      try {
        await AiApi.coolify.updateApp({
          projectExid: this.selectedProjectExid,
          cameraExid: this.selectedCameraExid,
          appId: this.appData.id,
          appUuid: this.appData.coolifyServiceUuid,
          payload: this.EditedObject,
        })
        this.$notifications.success("App updated successfully")
      } catch (error) {
        this.$notifications.error({
          text: "Failed to update app",
          error,
        })
      } finally {
        this.isLoading = false
      }
    },
    async createAppOnCoolify() {
      this.isLoading = true
      try {
        if (!this.shouldUpdateProjectFeatureFlags) {
          await this.UpdateProjectFeatureFlags(this.selectedProjectExid, {
            featureFlags: [
              ...this.selectedProjectFeatureFlags,
              ProjectFeatureFlag.GateReport,
              ProjectFeatureFlag.GateReportAnprBased,
            ],
          })
        }

        if (!this.cameraHasAnprFeatureFlag) {
          await this.updateCameraFeatureFlags(this.selectedCameraExid, {
            featureFlags: [
              ...this.anprCamera.featureFlags,
              CameraFeatureFlag.ANPR,
            ],
          })
        }

        if (!this.cameraHasGateReportFeatureFlag) {
          await this.updateCameraFeatureFlags(this.selectedContextCameraExid, {
            featureFlags: [
              ...this.contextCamera.featureFlags,
              CameraFeatureFlag.GateReport,
            ],
          })
        }
        const params: CreateCoolifyAppRequestPayload = {
          cameraExid: this.selectedCameraExid,
          serverUuid: this.selectedServerUuid,
          branch: this.branchName,
          snapshotRequestTimeout: this.snapshotRequestTimeout,
          modelVersion: this.modelVersion,
          startDate: this.startTime,
        }

        const result = await AiApi.coolify.createAppOnCoolify(params)
        this.coolifyServiceId = result.uuid
        await this.saveAppToCoolify()
        this.$notifications.success("App created successfully")
      } catch (error) {
        this.$notifications.error({
          text: "Failed to create app",
          error,
        })
        this.isLoading = false
      } finally {
        this.isLoading = false
      }
    },
    async saveAppToCoolify() {
      const params: SaveCoolifyAppRequestPayload = {
        contextCameraExid: this.selectedContextCameraExid,
        serverUuid: this.selectedServerUuid,
        branchName: this.branchName,
        snapshotRequestTimeout: this.snapshotRequestTimeout.toString(),
        modelVersion: this.modelVersion,
        startTime: this.startTime,
        coolifyServiceId: this.coolifyServiceId,
      }
      try {
        await AiApi.coolify.saveAppOnCoolify(
          this.selectedProjectExid,
          this.selectedCameraExid,
          params
        )
        this.$notifications.success("App created successfully")
      } catch (error) {
        this.$notifications.error({
          text: "Failed to create app",
          error,
        })
      }
    },
    resetForm() {
      this.selectedProject = null
      this.selectedCameraExid = null
      this.selectedContextCameraExid = null
      this.selectedServerUuid = null
      this.branchName = "master"
      this.snapshotRequestTimeout = 300
      this.modelVersion = "2024-10-12"
      this.startTime = this.$moment().format("YYYY-MM-DDTHH:mm:ss")
    },
    onEditApp(key: string, value: unknown) {
      if (!this.editMode || !value) {
        return
      }

      if (this.appData[key] === value) {
        delete this.EditedObject[key]

        return
      }
      this.EditedObject[key] = value
    },
  },
})
</script>
